import { Modal } from 'almanac-ui/Modal'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { Text } from 'blaze-ui/Text/Text'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ImagePickerProviderProps } from 'components/ImagePickerModal/ImagePickerContextTypes'
import { ImagePickerProvider, useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { ScrollContainer } from 'components/ImagePickerModal/ScrollContainer/ScrollContainer'
import { ImageThumbnail } from 'components/ImagePickerModal/ImageThumbnail/ImageThumbnail'
import { SelectionCount } from 'components/ImagePickerModal/SelectionCount'
import { DeselectAllButton } from 'components/ImagePickerModal/DeselectAllButton'
import { ConfirmSelectionButton } from 'components/ImagePickerModal/ConfirmSelectionButton'
import UploadsStyles from 'components/ImagePickerModal/Uploads/Uploads.module.scss'
import { useState } from 'react'
import { useCompletedBrandKits } from 'hooks/queries/brandKits/useCompletedBrandKits'
import { Chip } from 'components/Chip/Chip'
import Styles from './AllImages.module.scss'
import { HighDetailedUpload } from 'blaze-ui/icons'
import { BrandKitLogo } from 'screens/BrandKit/BrandKitLogo/BrandKitLogo'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { MediaFiles } from 'components/ImagePickerModal/BrandKits/MediaFiles'

interface AllImagesProps {
  initialBrandKitId: number
  confirmLabel?: string
  onPressOutside?: () => void
}

interface MediaFilesGridProps {
  mediaFiles: IMediaFile[]
  hasMore: boolean
  onLoadMore: () => void
}

const MediaFilesGrid = ({ mediaFiles, hasMore, onLoadMore }: MediaFilesGridProps) => {
  const { selectedImages, toggleSelectedImage } = useImagePickerContext()

  return (
    <ScrollContainer>
      <div className={UploadsStyles.allUploadsGrid}>
        {mediaFiles.map((mediaFile) => (
          <div key={mediaFile.id} className={UploadsStyles.allUploadsItem}>
            <ImageThumbnail
              image={{ ...mediaFile, url: mediaFile.thumbnail_url as string }}
              onToggleSelect={toggleSelectedImage}
              isSelected={selectedImages.some((i) => i.url === mediaFile.thumbnail_url)}
              duration={mediaFile.duration}
            />
            <Text variant="smallList" lineClamp={1}>
              {mediaFile.title}
            </Text>
          </div>
        ))}
      </div>
      {hasMore && (
        <div className={UploadsStyles.infiniteLoader}>
          <InfiniteScroll hasMore={hasMore} loadMore={onLoadMore} />
        </div>
      )}
    </ScrollContainer>
  )
}

const AllImages = ({ confirmLabel, onPressOutside, initialBrandKitId }: AllImagesProps) => {
  const [selectedBrandKitId, setSelectedBrandKitId] = useState<number | undefined>(initialBrandKitId)
  const { data } = useCompletedBrandKits()
  const context = useImagePickerContext()
  const { selectedImages, onConfirm } = context

  const handleConfirm = () => {
    onConfirm(selectedImages, context)
  }

  return (
    <Modal.Root size="lg" onPressOutside={onPressOutside} aria-label="Uploaded Media">
      <Modal.Header
        title="Uploaded Media"
        actions={
          <div className={UploadsStyles.allUploadsActions}>
            {selectedImages.length > 0 && (
              <ResponsiveDesign.Desktop>
                <SelectionCount />
                <DeselectAllButton />
                <ConfirmSelectionButton onPress={handleConfirm} confirmLabel={confirmLabel} />
              </ResponsiveDesign.Desktop>
            )}
          </div>
        }
      />
      <div className={UploadsStyles.allUploads}>
        <div className={Styles.selection}>
          <Heading level={5}>Select a Brand Kit or Uploads</Heading>
          <div className={Styles.pills}>
            {data?.map((brandKit) => (
              <Chip
                icon={() => <BrandKitLogo brandKit={brandKit} size={16} />}
                deletable={false}
                key={brandKit.id}
                isSelected={selectedBrandKitId === brandKit.id}
                onChange={(isSelected) => isSelected && setSelectedBrandKitId(brandKit.id)}
              >
                {brandKit.name}
              </Chip>
            ))}
            <Chip
              icon={HighDetailedUpload}
              deletable={false}
              isSelected={selectedBrandKitId === undefined}
              onChange={(isSelected) => isSelected && setSelectedBrandKitId(undefined)}
            >
              Uploaded Media
            </Chip>
          </div>
        </div>
        <MediaFiles brandKitId={selectedBrandKitId}>
          {({ mediaFiles, fetchNextPage, hasNextPage }) => (
            <MediaFilesGrid onLoadMore={fetchNextPage} hasMore={hasNextPage || false} mediaFiles={mediaFiles} />
          )}
        </MediaFiles>
      </div>
      {selectedImages.length > 0 && (
        <ResponsiveDesign.Mobile>
          <Modal.Footer>
            <Modal.FooterContent slot="left">
              <DeselectAllButton />
            </Modal.FooterContent>
            <Modal.FooterContent slot="right">
              <ConfirmSelectionButton onPress={handleConfirm} confirmLabel={confirmLabel} />
            </Modal.FooterContent>
          </Modal.Footer>
        </ResponsiveDesign.Mobile>
      )}
    </Modal.Root>
  )
}

interface Props extends AllImagesProps, Omit<ImagePickerProviderProps, 'children'> {}

const AllImagesContainer = ({ confirmLabel, onPressOutside, initialBrandKitId, ...props }: Props) => (
  <ImagePickerProvider {...props}>
    <AllImages confirmLabel={confirmLabel} onPressOutside={onPressOutside} initialBrandKitId={initialBrandKitId} />
  </ImagePickerProvider>
)

export { AllImagesContainer as AllImages }
